/**
 * URL sanitisation. Full hrefs frequently carry secrets in the query string or
 * hash — OAuth tokens (#access_token=…), password-reset ?token=…, magic links,
 * emails in unsubscribe links. We keep origin + path (needed for analytics) but
 * redact sensitive query params and drop the hash entirely.
 */

const SENSITIVE_PARAM_RE =
  /(^|_)(token|access|refresh|id_token|auth|secret|password|passwd|pwd|code|state|key|apikey|api_key|session|sig|signature|email|otp|reset|verify|invite|ticket|jwt)($|_)/i;

const REDACTED = "__redacted__";

/** Scrub a URL: strip hash, redact sensitive query params, keep origin+path. */
export function scrubUrl(input: string | undefined | null): string | undefined {
  if (!input) return undefined;
  try {
    const u = new URL(input, location.href);
    u.hash = "";
    for (const key of Array.from(u.searchParams.keys())) {
      if (SENSITIVE_PARAM_RE.test(key)) u.searchParams.set(key, REDACTED);
    }
    return u.toString();
  } catch {
    // Not a parseable URL — strip anything after ? or # defensively.
    return input.split(/[?#]/)[0];
  }
}

/** Path only (no query/hash) — safe for analytics keys. */
export function scrubPath(input: string | undefined | null): string | undefined {
  if (input == null) return undefined;
  try {
    return new URL(input, location.href).pathname;
  } catch {
    return input.split(/[?#]/)[0];
  }
}
